import * as React from 'react';
import FroalaEditorComponent from 'react-froala-wysiwyg';

// Require Editor CSS files
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';

// Import all Froala Editor plugins
import 'froala-editor/js/plugins.pkgd.min.js';

// Include font-awesome css for editor icons
import 'font-awesome/css/font-awesome.css';

import { cn } from '@/lib/utils';

/**
 * FroalaEditor Props
 * Extends standard FroalaEditorComponent props with additional customization
 */
export interface FroalaEditorProps {
  /** Current editor content (HTML string) */
  value?: string;
  /** Callback fired when editor content changes */
  onChange?: (content: string) => void;
  /** Additional CSS classes */
  className?: string;
  /** Froala editor configuration options */
  config?: Record<string, unknown>;
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * FroalaEditor - WYSIWYG rich text editor component
 *
 * A wrapper around Froala Editor that integrates with React Hook Form
 * and matches the styling of ShadCN UI components.
 *
 * @example
 * ```tsx
 * <FroalaEditor
 *   value={content}
 *   onChange={setContent}
 *   placeholder="Enter content here..."
 * />
 * ```
 */
const FroalaEditor = React.forwardRef<HTMLDivElement, FroalaEditorProps>(
  ({ className, value = '', onChange, config = {}, placeholder, disabled, ...props }, ref) => {
    // Default Froala configuration
    const defaultConfig = {
      placeholderText: placeholder || 'Edit Your Content Here!',
      charCounterCount: true,
      heightMin: 300,
      heightMax: 600,
      quickInsertEnabled: false,
      toolbarButtons: [
        'bold',
        'italic',
        'underline',
        'strikeThrough',
        'textColor',
        'backgroundColor',
        'formatOL',
        'formatUL',
        'paragraphFormat',
        'alignLeft',
        'alignCenter',
        'alignRight',
        'insertLink',
        'undo',
        'redo',
      ],
      key: 'xGE6oF4B3B3B9D9D5F4gKTRe1CD1PGb1DESAb1Kd1EBH1Pd1TKoD6C5G5A4G2C3H3B4B5C6==',
      // Merge with provided config
      ...config,
    };

    // Handle model change from Froala
    const handleModelChange = (content: string) => {
      if (onChange) {
        onChange(content);
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          'border-input focus-within:border-ring focus-within:ring-ring/50 dark:bg-input/30 w-full rounded-md border bg-transparent shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]',
          '[&_.fr-element]:!pl-4',
          '[&_.fr-element_ol]:list-decimal [&_.fr-element_ol]:ml-6',
          '[&_.fr-element_ul]:list-disc [&_.fr-element_ul]:ml-6',
          '[&_.fr-element_h1]:text-3xl [&_.fr-element_h1]:font-bold [&_.fr-element_h1]:my-4',
          '[&_.fr-element_h2]:text-2xl [&_.fr-element_h2]:font-bold [&_.fr-element_h2]:my-3',
          '[&_.fr-element_h3]:text-xl [&_.fr-element_h3]:font-bold [&_.fr-element_h3]:my-2',
          '[&_.fr-element_h4]:text-lg [&_.fr-element_h4]:font-bold [&_.fr-element_h4]:my-2',
          '[&_.fr-element_h5]:text-base [&_.fr-element_h5]:font-bold [&_.fr-element_h5]:my-1',
          '[&_.fr-element_h6]:text-sm [&_.fr-element_h6]:font-bold [&_.fr-element_h6]:my-1',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        {...props}
      >
        <FroalaEditorComponent
          tag="textarea"
          model={value}
          onModelChange={handleModelChange}
          config={defaultConfig}
        />
      </div>
    );
  }
);

FroalaEditor.displayName = 'FroalaEditor';

export { FroalaEditor };
